import dayjs from 'dayjs';
import Vue from 'vue';
import Vuex from 'vuex';
import VueResource from 'vue-resource';
import { v4 as uuidv4 } from 'uuid';

import {
	AppsUsageItem, AppUsageAction, AppUsageSession,
	DisplayedLanguage, DocumentationLibrary, DocumentationPackage, DocumentationLinkItem, DocumentationLinksList,
	DocumentCategory, DocumentCertification, DocumentationCertificationsPackages, DocumentLanguage, DocumentSubcategory, DocumentVersion,
	ProductFamily, ProductSeries, ProductSeriesStatus, ProductVersion,
	SPUser, UserDocumentComment, UserDocumentNote, UserNoteItem,
} from './classes.js';

import { VueResourceUrls, VueResourceData, VueResourceHeaders } from './vue-resource.js';
import { BasePermissionEnum, SharePointList } from './sp-classes.js';
import { utilities } from './utilities.js';
import { CONSTANTS } from './constants.js';
import BatchPromise from './batchPromise.js';

Vue.use(Vuex);
Vue.use(VueResource);
Vue.http.options.root = 'https://salicru.sharepoint.com/sites/intranet/';

require('es6-promise').polyfill();

export const store = new Vuex.Store({
	state: {
		stateMachine: {
			appVersion: '2.0.20240118-hotfix-HD-765',
			currentFamily: null,
			devMode: false,
			fullScreenMode: false,
			accessError: false,
			initialized: false,
			isExternalUser: null,
			loaded: false,
			loadingInitialData: false,
			testMode: false,
			underDevelopmentVisible: false,
			verboseMode: false,
		},
		sites: {
			tenantUrl: 'https://salicru.sharepoint.com',
			intranetSiteColPath: '/sites/intranet',
			documentationSitePath: '/sites/intranet/documentation',
		},
		releaseNotes: {
			minimunTimeOnModalPages: 2500,
			features: [
				{
					featureDate: '20220101', names: ['generalViewFilters', 'searchForUpdates', 'productViewFilters', 'documentPackages', 'versionHistory', 'addEditLink'],
				},
			],
			releaseNotesUserInfor: null,
		},
		browserLanguage: null,
		allLanguagesIso6391: null,

		browser: null,
		currentUser: null,
		documentationCategories: [],
		documentationCertifications: [],
		documentationCertificationsPackages: [],
		documentationDocumentLibraries: null,
		documentationLinksLists: null,
		documentationPackages: null,
		documentationSubcategories: [],
		documentTypes: [],
		families: [],
		publicIp: null,
		localIp: null,
		languages: [],
		localStorageData: [],
		searchForUpdatesDoneOnce: false,
		series: [],
		seriesStatus: [],
		spUsers: null,
		userLastVisit: null,
		userNotes: null,
		usageTrackerStorage: null,
		usageTrackerList: null,
		usageTrackerListAllUsers: null,
		uuid: null,
		versions: [],
	},

	getters: {
		documentationLanguagesDisplayed: (state) => state.allLanguagesIso6391.filter((dl) => dl.isDisplayed),
		documentationLanguagesDisplayedTitle: (state) => state.allLanguagesIso6391.filter((dl) => dl.isDisplayed).map((dl) => dl.title),
		documentationSubcategoriesDisplayed: (state) => state.documentationSubcategories.filter((ds) => ds.isDisplayed),
		documentationSubcategoriesDisplayedIds: (state) => state.documentationSubcategories.filter((ds) => ds.isDisplayed).reduce((array, item) => {
			array.push(item.id);
			return array;
		}, []),
		documentationCertificationsDisplayedIds: (state) => state.documentationCertifications.filter((c) => c.isDisplayed).reduce((array, item) => {
			array.push(item.id);
			return array;
		}, []),

		documentationCertificationsSubcategoriesDisplayed: (state, getters) => state.documentationCertificationsPackages.filter((dcp) => getters.documentationCertificationsDisplayedIds.includes(...dcp.certificationId)).reduce((array, item) => {
			array.push(...item.subcategoriesIds);
			return array;
		}, []),

		// https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
		// Googling for browser reliable detection often results in checking the User agent string. This method is not reliable, because it's trivial to spoof this value
		getBrowser: () => {
			if (!!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)) {
				if (navigator.userAgent.indexOf('Edg') !== -1) {
					return 'Egde Chromium';
				}
				return 'Chrome';
			}

			if (false || !!document.documentMode) {
				return 'IE';
			}

			if (!(false || !!document.documentMode) && !!window.StyleMedia) {
				return 'Edge';
			}

			if (typeof InstallTrigger !== 'undefined') {
				return 'Firefox';
			}

			if ((!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0) {
				return 'Opera';
			}

			if (/constructor/i.test(window.HTMLElement) || ((p) => p.toString() === '[object SafariRemoteNotification]')(!window.safari || (typeof safari !== 'undefined' && window.safari.pushNotification))) {
				return 'Safari';
			}

			return 'undefined';
		},

		getDaysForSearchUpdates: (state) => {
			const lastVisitDate = dayjs(state.userLastVisit),
				daysForSubstract = dayjs().diff(lastVisitDate, 'days') >= CONSTANTS.MinimumDaysToCheckForUpdates ? 0 : CONSTANTS.MinimumDaysToCheckForUpdates;


			return lastVisitDate.subtract(daysForSubstract, 'days');
		},

		getLanguageAcronymScreen: (state) => (payload) => {
			let l = state.languages.find((lang) => lang.id === payload.languageId);

			return l ? l.isoName.split(' // ').join('\n') : '';
		},

		getLanguageAcronymFile: (state) => (payload) => {
			let l = state.languages.find((lang) => lang.id === payload.languageId);
			return l.pendriveCode;
		},

		getLanguageByCode: (state) => (code) => state.languages.find((l) => l.code === code),

		isCurrentUserExternalUser: (state) => {
			if (!state.currentUser || !state.currentUser.userPrincipalName) return false;
			return state.currentUser.userPrincipalName.match(/^(official.distributor@|mechanical.supplier@|documentation.provider@)/gm) !== null;
		},

		userListReleaseNotesCompleted: (state) => true, // Release notes are disabled
		//(state.stateMachine.appVersion.split('.')[2] < state.userLastVisit && state.releaseNotes.releaseNotesUserInfor
		// 	&& state.releaseNotes.releaseNotesUserInfor.dateTutorialCompleted !== null)
		// || (state.stateMachine.appVersion.split('.')[2] === state.userLastVisit && state.userLastVisit && state.releaseNotes.releaseNotesUserInfor
		// 	&& state.releaseNotes.releaseNotesUserInfor.dateTutorialCompleted >= state.stateMachine.appVersion.split('.')[2]),
	},

	mutations: {
		applyDocumentationPackage(state, subcategoriesIds) {
			state.documentationSubcategories.forEach((sc) => {
				sc.isDisplayed = subcategoriesIds.includes(sc.id);
			});
		},

		applyDocumentationCertificationsPackage(state, { certificationsId }) {
			state.documentationCertifications.forEach((o) => {
				o.isDisplayed = certificationsId.includes(o.id);
			});
		},

		getLocalStorageData: (state) => {
			state.localStorageData = JSON.parse(localStorage.getItem(CONSTANTS.UserSettings.localStorageKey) || '{}');
			let storedData;


			storedData = state.localStorageData.filtersSubcategory || [];
			state.documentationSubcategories.forEach((sc) => {
				sc.isDisplayed = storedData.length > 0 ? storedData.includes(sc.id) : sc.isDisplayed;
			});
			storedData = state.localStorageData.filtersLanguage || [];
			state.allLanguagesIso6391.forEach((l) => {
				l.isDisplayed = storedData.length > 0 ? storedData.includes(l.title) : false;
			});
			storedData = state.localStorageData.familySettings || {};
			if (storedData) {
				state.families.forEach((f) => {
					if (Object.prototype.hasOwnProperty.call(storedData, f.id)) {
						f.isDisplayed = storedData[f.id].isDisplayed !== undefined ? storedData[f.id].isDisplayed : storedData[f.id];
						f.displayDiscontinuedProducts = storedData[f.id].displayDiscontinuedProducts !== undefined ? storedData[f.id].displayDiscontinuedProducts : storedData.displayDiscontinuedProducts;
						f.displayOnlyPendriveSeries = storedData[f.id].displayOnlyPendriveSeries !== undefined ? storedData[f.id].displayOnlyPendriveSeries : storedData.displayOnlyPendriveSeries;
						f.searchForUpdates = storedData[f.id].searchForUpdates !== undefined ? storedData[f.id].searchForUpdates : storedData.searchForUpdates;
					}
				});
			}
			state.userLastVisit = state.localStorageData.userLastVisit;

			state.localStorageData.userLastVisit = dayjs().format('YYYYMMDD');
			localStorage.setItem(CONSTANTS.UserSettings.localStorageKey, JSON.stringify(state.localStorageData));

			state.releaseNotes.releaseNotesUserInfor = state.localStorageData.releaseNotes;

			state.usageTrackerStorage = state.localStorageData.usageTracker;
		},

		initUsageTracker: (state) => {
			Vue.prototype.$usageTracker.initialize({
				localStorageId: CONSTANTS.UserSettings.localStorageKey,
				usageListItems: state.usageTrackerList,
				usageTrackerInfo: state.usageTrackerStorage,
			});
		},

		setAllCategoryVisibility: (state, { id, value }) => {
			state.documentationSubcategories.filter((dsc) => dsc.category.id === id).forEach((dsc) => {
				dsc.isDisplayed = value;
				dsc.category.isDisplayed = value;
			});
		},

		setAllDisplayedLanguagesVisibility: (state, value) => {
			state.allLanguagesIso6391.forEach((dl) => {
				dl.isDisplayed = value;
			});
		},

		setAllLanguagesIso6391(state) {
			state.allLanguagesIso6391 = [...new Set(state.languages.map((l) => l.title.match(/\b[A-Z]{2}\b/gi)).reduce((languagesArray, languagesRegexResultArray) => {
				if (languagesRegexResultArray) {
					languagesArray.push(...languagesRegexResultArray);
				}
				return languagesArray;
			}, []))].map((l) => new DisplayedLanguage(l)).sort(utilities.multiCriteriaSort('title'));
		},

		setAllSubcategoryVisibility: (state, value) => {
			state.documentationSubcategories.forEach((sb) => {
				let auxValue = value;
				if (value && sb.id === CONSTANTS.OtherDocuments.subcategoryId) auxValue = false;

				sb.isDisplayed = auxValue;
				sb.category.isDisplayed = auxValue;
			});
		},

		setAllCertificationsVisibility: (state, value) => {
			state.documentationCertifications.forEach((sb) => {
				sb.isDisplayed = value;
			});
		},

		setCategoryDisplayed: (state) => {
			state.documentationCategories.forEach((c) => {
				const some = state.documentationSubcategories.some((sc) => sc.category.id === c.id && sc.isDisplayed);
				c.isDisplayed = some;
			});
		},

		setCurrentFamily(state, family) {
			state.stateMachine.currentFamily = family;
		},

		setCurrentUser(state, getters) {
			const user = state.spUsers.find((u) => u.userPrincipalName === state.currentUserId);
			if (user) {
				state.currentUser = user;
				state.currentUser.isExternal = getters.isCurrentUserExternalUser;
			} else {
				state.currentUser = {
					userPrincipalName: state.currentUserId,
					displayName: state.currentUserId,
					email: state.currentUserId,
					isExternal: true
				};
			}
		},

		setCurrentUserId(state, user) {
			state.currentUserId = user.userName;// _spPageContextInfo.userId;
		},

		setHeaderToken(state, token) {
			const safeToken = typeof token === 'object' ? (token.token || token.accessToken) : token;
			state.stateMachine.token = safeToken;
			VueResourceHeaders.token = safeToken;
		},

		setLanguageVisibility: (state, { title, value }) => {
			state.allLanguagesIso6391.find((dl) => dl.title === title).isDisplayed = value;
		},

		setStateVariables(state) {
			let getQueryVariable = function (variable) {
				let query = window.location.search.substring(1),
					vars = query.split('&'), n = vars.length, i, pair;

				for (i = 0; i < n; i++) {
					pair = vars[i].split('=');
					if (pair[0] === variable) {
						return pair[1];
					}
				}

				return false;
			};

			state.stateMachine.verboseMode = (getQueryVariable('verbose') === '1');
			state.stateMachine.testMode = (getQueryVariable('test') === '1');
			state.stateMachine.devMode = (getQueryVariable('dev') === '1');
			state.stateMachine.fullScreenMode = (getQueryVariable('fullscreen') === '1');
			state.stateMachine.underDevelopmentVisible = (getQueryVariable('underDevelopment') === '1') || (getQueryVariable('underdevelopment') === '1');
		},

		setSubcategoryVisibility: (state, { id, value }) => {
			state.documentationSubcategories.find((sb) => sb.id === id).isDisplayed = value;
		},

		setUsageTrackerStorage(state, data) {
			state.usageTrackerStorage = data;
		},

		setUserSettings(state, { field, array }) {
			if (state.stateMachine.verboseMode) console.log(`[PRODUCTS] Data saved on localStorage[ ${field}:${JSON.stringify(array)}]`);

			state.localStorageData[field] = array;
			localStorage.setItem(CONSTANTS.UserSettings.localStorageKey, JSON.stringify(state.localStorageData));
		},

		transferCookiesToLocalStorage(state) {
			let cookieArr = decodeURIComponent(document.cookie).split('; '),
				phCookies = cookieArr.filter((c) => c.split('=')[0] === 'products-homepage'),
				resultObjs = phCookies && phCookies.map((phCookie) => JSON.parse(phCookie.split('=')[1]));

			if (resultObjs) {
				// in some cases exists diffent cookies with the same key, but diferent path and different content
				// we only are intereted with the format:
				// {"displayDiscontinuedProducts":true,"displayOnlyPendriveSeries":false,"displayOnlyMostRecentVersion":false,"daysToCheckForUpdates":7,"1":true,"2":false,"3":true,"4":true,"5":true,"6":true,"7":true,"8":true,"9":true,"10":true}
				resultObjs.forEach((resultObj) => {
					if (Object.keys(resultObj).some((k) => !isNaN(k))) {
						state.localStorageData = JSON.parse(localStorage.getItem(CONSTANTS.UserSettings.localStorageKey) || '{}');
						state.localStorageData.familySettings = resultObj;
						localStorage.setItem(CONSTANTS.UserSettings.localStorageKey, JSON.stringify(state.localStorageData));
						if (state.stateMachine.verboseMode) console.log('[PRODUCTS] getCookies', state.cookies);
					}
				});
				// in some cases exists diffent cookies with the same key, but diferent path
				// this is for erase all cookies, no care about the path
				document.cookie = 'products-homepage=; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
				document.cookie = 'products-homepage=; path=/sites/intranet/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
				document.cookie = 'products-homepage=; path=/sites/intranet/products/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
				document.cookie = 'products-homepage=; path=/sites/intranet; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
				document.cookie = 'products-homepage=; path=/sites/intranet/products; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
			}
		},

		updateUserNotesSeriesDocumentsLinkList(state, { document, seriesId }) {
			if (!state.userNotes) return;

			const documentNote = state.userNotes.documentsNotes.filter((un) => un.documentName === document.title).shift();

			// set default values
			document.comment = null;
			document.isFavorite = false;
			document.isFavoriteInThisSeries = false;

			// update values
			if (documentNote && documentNote.favoriteSeriesIds && documentNote.favoriteSeriesIds.length) {
				document.isFavorite = true;
				document.isFavoriteInThisSeries = documentNote.favoriteSeriesIds.includes(seriesId);
			}

			if (documentNote && documentNote.comment) {
				document.comment = documentNote.comment;
			}
		},

		updateUserNotes(state, { seriesId, name, commentText, isFavourite }) {
			if (!state.userNotes) {
				state.userNotes = new UserNoteItem({
					Title: state.currentUser.email,
					UserId: state.currentUser.id,
					JSON: JSON.stringify({ documentsNotes: [] }),
				});
			}

			let documentNote = state.userNotes.documentsNotes.filter((dn) => dn.documentName === name).shift(),
				comment = (documentNote && documentNote.comment) ? documentNote.comment : null,
				favoriteSeriesIds = (documentNote && documentNote.favoriteSeriesIds) ? documentNote.favoriteSeriesIds : [],
				favoriteIndex = favoriteSeriesIds.indexOf(seriesId);

			if (isFavourite) {
				if (favoriteIndex !== -1) {
					favoriteSeriesIds.splice(favoriteIndex, 1);
				} else {
					favoriteSeriesIds.push(seriesId);
				}

				// this line is for link the seriesID in the documentNote object when that exists only with comments
				if (documentNote) documentNote.favoriteSeriesIds = favoriteSeriesIds;
			} else if (commentText === '') {
				documentNote.comment = null;
			} else {
				if (!comment) {
					comment = new UserDocumentComment({ text: commentText, dateYMD: dayjs().format('YYYYMMDD') });
				} else {
					comment.text = commentText;
					comment.dateYMD = dayjs().format('YYYYMMDD');
				}

				// this line is for link the commnets in the documentNote object when that exists only with favorite id's
				if (documentNote && !documentNote.comment) {
					documentNote.comment = comment;
				}
			}

			if (!documentNote) {
				documentNote = new UserDocumentNote({ documentName: name, favoriteSeriesIds, comment });
				state.userNotes.documentsNotes.push(documentNote);
			}

			// when create a new documentNoteItem, some fields are null, delete them so as not to save them in SPL
			if (!documentNote.comment) delete documentNote.comment;
			if (documentNote.favoriteSeriesIds && !documentNote.favoriteSeriesIds.length) delete documentNote.favoriteSeriesIds;
		},

		updateUsageTrackerStorage(state, { field, data, localIp }) {
			if (!state.currentUser.isExternal) {
				state.usageTrackerStorage = state.usageTrackerStorage || {};
				const date = dayjs().utc(), dateYMD = date.format('YYYYMMDD'), dateHMS = date.format('HH:MM:SS'),
					action = new AppUsageAction({ actionName: field, actionData: data, dateHMS });

				let currentDateSessionArray = state.usageTrackerStorage[dateYMD] = state.usageTrackerStorage[dateYMD] || [],
					session = currentDateSessionArray.find((o) => o.uuid === state.uuid);

				if (session === undefined) {
					session = new AppUsageSession({
						actions: [],
						browser: state.browser,
						publicIp: state.publicIp,
						localIp,
						uuid: state.uuid,
					});
					currentDateSessionArray.push(session);
				}

				session.actions.push(action);

				// is not needed saved the user
				state.usageTrackerStorage.user = { id: state.currentUser.id, displayName: state.currentUser.displayName };
				state.localStorageData.usageTracker = state.usageTrackerStorage;
				localStorage.setItem(CONSTANTS.UserSettings.localStorageKey, JSON.stringify(state.localStorageData));
				if (state.stateMachine.verboseMode) console.log(`[PRODUCTS] Usage tracker data saved [ ${JSON.stringify(action)}]`);
			}
		},
	},

	actions: {
		downloadDocuments({ state }, { arrayDocumentsTitle, seriesVersion, seriesName }) {
			let restCallUrl = VueResourceUrls.downloadDocuments(),
				restCallData = VueResourceData.downloadDocuments({ arrayDocumentsTitle, seriesVersion, seriesName }, state.currentUser),
				restCallHeaders = VueResourceHeaders.standardGetHeadersWithOutToken(); // standardGetHeaders because it's a flow

			return new Promise((resolve, reject) => {
				Vue.http.post(restCallUrl, restCallData, restCallHeaders).then((responseDD) => {
					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] downloadDocuments request finished', responseDD);
					resolve(responseDD);
				}).catch((error) => {
					console.error('[PRODUCTS] Error at downloadDocuments', error);
					reject(error);
				});
			});
		},

		flushUsageTrackerInfo({ state, commit, dispatch, getters }) {
			if (!getters.isCurrentUserExternalUser) {
				dispatch('getContextInfo').then(() => {
					Vue.prototype.$usageTracker.flush().then((response) => {
						if (state.stateMachine.verboseMode) console.log('[PRODUCTS] flushUsageTrackerInfo finished');
						if (response) {
							commit('setUsageTrackerStorage', response);
							commit('setUserSettings', { field: 'usageTracker', array: response });
						}
					}).catch((error) => console.error('[PRODUCTS] Error at flushUsageTrackerInfo', error));
				});
			}
		},

		getAllSeriesVersionsContainingDocumentLink({ state }, { documentTitle }) {
			if (state.stateMachine.testMode) console.warn('[PRODUCTS] getAllSeriesVersionsContainingDocumentLink - Remember that data may be inconsistent if you are using test = 1');

			const splistsFiltered = state.stateMachine.testMode ? state.versions.filter((v) => v.id === 9 || v.id === 78 || v.id === 95) : state.versions,
				splists = splistsFiltered.map((version) => new SharePointList(true, ['playground/', 'products/DocumentationLinks/'], `${version.series.url}_${version.title}`, `${version.series.url}_${version.title}`)),
				filter = `(Title eq '${documentTitle}')`,
				restCallUrls = splists.map((spl) => VueResourceUrls.getAllSeriesVersionsContainingDocumentLink(state.stateMachine.testMode, spl, filter));

			return new Promise((resolve) => {
				BatchPromise.call({ urls: { get: restCallUrls } }).then((responseGSLDD) => {
					const arrayVersionsId = responseGSLDD.reduce((acum, response, index) => (response.length ? acum.concat({ versionId: splistsFiltered[index].id, documentId: response[0].Id, documentTitle: response[0].Title }) : acum), []);
					resolve(arrayVersionsId);
				});
			});
		},

		getContextInfo({ state }) {
			return new Promise((resolve, reject) => {
				if (!VueResourceHeaders.formDigestValueDate || (dayjs().diff(VueResourceHeaders.formDigestValueDate, 'minutes') >= 5)) {
					let restCallUrl = VueResourceUrls.getContextInfo(),
						restCallHeaders = VueResourceHeaders.standardGetHeaders();

					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] getContextInfo', restCallUrl, restCallHeaders);

					Vue.http.post(restCallUrl, null, restCallHeaders).then((responseGCI) => {
						const formDigestValue = responseGCI.data.GetContextWebInformation ? responseGCI.data.GetContextWebInformation.FormDigestValue : responseGCI.data.FormDigestValue;

						if (state.stateMachine.verboseMode) console.log(`[PRODUCTS] getContextInfo load finished. New formDigestValue: ${formDigestValue}`);

						VueResourceHeaders.formDigestValue = formDigestValue;
						VueResourceHeaders.formDigestValueDate = dayjs();

						resolve();
					}).catch((error) => {
						console.error('[PRODUCTS] Error at getContextInfo', error);
						reject(error);
					});
				} else {
					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] getContextInfo no call needed');
					resolve();
				}
			});
		},

		getDocumentationLinkItems({ state, dispatch }, { version }) {
			let restCallUrl = VueResourceUrls.getDocumentationLinkItems(state.stateMachine.testMode, version.documentationLinksList),
				restCallHeaders = VueResourceHeaders.standardGetHeaders();

			if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Starting getDocumentationLinkItems', restCallUrl);

			return new Promise((resolve, reject) => {
				version.documentationLinksList.contentLoading = true;
				Vue.http.get(restCallUrl, restCallHeaders).then((responseGDLI) => {
					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Finished getDocumentationLinkItems', responseGDLI);

					version.documentationLinksList.content = responseGDLI.data.value.map((row) => new DocumentationLinkItem(row))
						// TODO: fix error in the next case: when de document link item has a wrong name by prefix (like UN instead of IN),
						// try to acces to hasPermissions function in undefined item.
						.filter((dll) => {
							let dl = state.documentationDocumentLibraries.find((ddl) => ddl.internalName(state.stateMachine.testMode) === dll.libraryName);

							if (dl) return dl.hasPermission(BasePermissionEnum.ViewListItems);

							return true;
						})
						.sort(utilities.multiCriteriaSort('title'));
					dispatch('getDocumentsDescriptions', { items: version.documentationLinksList.content }).then((responsesGDD) => {
						version.documentationLinksList.contentLoading = false;
						version.documentationLinksList.contentLoaded = true;

						resolve(responsesGDD.reduce((total, el) => total && el, true));
					}).catch((error) => reject(error));
				}).catch((error) => {
					console.error('[PRODUCTS] Error at getDocumentationLinkItems', error);
					reject(error);
				});
			});
		},

		getDocumentsDescriptions({ state, dispatch }, { items }) {
			return new Promise((resolve, reject) => {
				let allLibraries = [...new Set(items.map((item) => item.libraryName))];

				if (state.stateMachine.verboseMode) console.log(`[PRODUCTS] getDocumentsDescriptions found ${allLibraries.length} different libraries`, allLibraries);

				Promise.all(allLibraries.map((lib) => dispatch('getSingleLibraryDocumentsDescriptions', { libraryName: lib, items }))).then((responses) => {
					resolve(responses);
				}).catch((error) => reject(error));
			});
		},

		getDocumentVersions({ state }, { spList, item }) {
			let restCallUrl = VueResourceUrls.getDocumentVersions(state.stateMachine.testMode, spList, item.uniqueId),
				restCallHeaders = VueResourceHeaders.standardGetHeaders();

			return new Promise((resolve, reject) => {
				Vue.http.get(restCallUrl, restCallHeaders).then((response) => {
					response.data.Versions.forEach((v) => {
						item.documentVersions.forEach((dv) => {
							if (dv.versionId === v.OData__x005f_UIVersion) {
								dv.description = v.Title;
								dv.isCurrentVersion = v.IsCurrentVersion;
								dv.editorId = v.Editor.LookupId;

								dv.relink();
							}
						});
					});
					resolve();
				}).catch((error) => {
					console.error('[PRODUCTS] Error at getDocumentVersions', error);
					reject(error);
				});
			});
		},

		getPublicIp({ state }) {
			return new Promise((reject) => {
				fetch('https://api.ipify.org?format=json').then((x) => x.json()).then(({ ip }) => {
					state.publicIp = ip;
				}).catch((e) => reject(e));
			});
		},

		getLocalIP() {
			return new Promise((resolve, reject) => {
				// NOTE: window.RTCPeerConnection is "not a constructor" in FF22/23
				let RTCPeerConnection = /* window.RTCPeerConnection || */ window.webkitRTCPeerConnection || window.mozRTCPeerConnection,
					rtc = new RTCPeerConnection({ iceServers: [] }),
					addrs = {};

				if (!RTCPeerConnection) {
					reject('Your browser does not support this API');
				}

				addrs['0.0.0.0'] = false;

				function grepSDP(sdp) {
					let finalIP = '';

					sdp.split('\r\n').forEach((line) => { // c.f. http://tools.ietf.org/html/rfc4566#page-39
						if (~line.indexOf('a=candidate')) { // http://tools.ietf.org/html/rfc4566#section-5.13
							let parts = line.split(' '), // http://tools.ietf.org/html/rfc5245#section-15.1
								addr = parts[4],
								type = parts[7];
							if (type === 'host') {
								finalIP = addr;
							}
						} else if (~line.indexOf('c=')) { // http://tools.ietf.org/html/rfc4566#section-5.7
							let parts = line.split(' '),
								addr = parts[2];
							finalIP = addr;
						}
					});
					return finalIP;
				}

				if (1 || window.mozRTCPeerConnection) { // FF [and now Chrome!] needs a channel/stream to proceed
					rtc.createDataChannel('', { reliable: false });
				}

				rtc.onicecandidate = (evt) => {
					// convert the candidate to SDP so we can run it through our general parser
					// see https://twitter.com/lancestout/status/525796175425720320 for details
					if (evt.candidate) {
						let addr = grepSDP(`a=${evt.candidate.candidate}`);
						resolve(addr);
					}
				};
				rtc.createOffer((offerDesc) => {
					rtc.setLocalDescription(offerDesc);
				}, (e) => { console.warn('offer failed', e); });
			});
		},

		getSeriesContents({ state, commit, dispatch }, { series }) {
			return new Promise((resolve, reject) => {
				if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Starting getSeriesContents', series);

				Promise.all([
					dispatch('getSeriesSiteLists', { series }),
					...series.versions.map((v) => dispatch('getVersionContents', { version: v })),
				]).then((responses) => {
					let pictLibrariesNames = ['SlideshowPictures', 'LoResPictures', 'HiResPictures', 'Videos'],
						versionKeyNames = ['slideshowPicturesLibrary', 'loResPicturesLibrary', 'hiResPicturesLibrary', 'videosPicturesLibrary'],
						siteLists = responses[0].data.value,
						libraryTitle, versionKeyName;

					series.versions.forEach((v) => {
						pictLibrariesNames.forEach((pln, plnIdx) => {
							libraryTitle = `${pln}${v.title}`;
							versionKeyName = versionKeyNames[plnIdx];

							v[versionKeyName] = siteLists.find((r) => r.Title === libraryTitle) && new SharePointList(false, `products/${v.series.url}/`, libraryTitle);
						});
					});

					series.versions.forEach((v) => v.documentationLinksList.content.forEach((d) => {
						commit('updateUserNotesSeriesDocumentsLinkList', { document: d, seriesId: series.id });
					}));



					series.contentsLoaded = true;
					resolve();
				}).catch((error) => reject(error));
			});
		},

		getSeriesSiteLists({ state }, { series }) {
			return new Promise((resolve, reject) => {
				if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Starting getSeriesSiteLists', series);

				Vue.http.get(VueResourceUrls.getSeriesSiteLists(series), VueResourceHeaders.standardGetHeaders()).then((responses) => {
					resolve(responses);
				}).catch((error) => {
					console.error('[PRODUCTS] Error at getSeriesSiteLists', error);
					reject(error);
				});
			});
		},

		getUsageTrackerSessionInfo({ state, getters, dispatch }) {
			state.browser = getters.getBrowser;
			state.uuid = uuidv4();

			dispatch('getPublicIp').catch((e) => console.error('[PRODUCTS] Error at getUsageTrackerSessionInfo', e));
		},

		getSingleLibraryDocumentsDescriptions({ state }, { libraryName, items }) {
			let libraryItems = items.filter((i) => i.libraryName === libraryName),
				filter = libraryItems.map((i) => {
					//because other documents exists documents encoded with extension in lower case (pdf), but in other libraries are encoded
					// with upper case, and also, in other library exists documents with a very similar names, like Title.pdf and Title(1).pdf
					//needs to add character '.' for search a complete name, but avoid the extension
					if (i.otherDocuments) return `(startswith(Name,'${encodeURIComponent(i.title)}.'))`;

					return `(Name eq '${i.title}.PDF')`;
				}),
				spList = new SharePointList(false, 'documentation/', libraryName),
				// restCallUrl = VueResourceUrls.getSingleLibraryDocumentsDescriptions(state.stateMachine.testMode, spList, filter),
				item,
				// .join(' or '),
				filterArray = [],
				urlLenght = 149,
				selectLenght = 161,
				filterAux = '',
				restCallUrl;

			filter.forEach((current, index) => {
				let nextLength = 0;

				filterAux += `${(filterAux === '' ? '' : '%20or%20')}${current}`;
				nextLength = filter[index + 1] === undefined ? 0 : filter[index + 1].length;
				// filterArray.push(filterAux);
				// acum.push(`${rcuSplited[0]}${filterAux}${rcuSplited[1]}`);

				if ((filterAux.length + urlLenght + selectLenght + nextLength) > 2048) {
					filterArray.push(filterAux);
					filterAux = '';
				}
			});

			filterArray.push(filterAux);

			restCallUrl = filterArray.map((f) => VueResourceUrls.getSingleLibraryDocumentsDescriptions(state.stateMachine.testMode, spList, f));

			if (state.stateMachine.verboseMode) console.log(`[PRODUCTS] getSingleLibraryDocumentsDescriptions found ${libraryItems.length} items in library ${libraryName}`, restCallUrl);

			return new Promise((resolve, reject) => {
				BatchPromise.call({ urls: { get: restCallUrl } }).then((responseGSLDD) => {
					let totalItems = 0;
					responseGSLDD.forEach((response) => {
						response.forEach((r) => {
							item = libraryItems.find((i) => (i.otherDocuments ? r.Name.startsWith(`${i.title}.`) : r.Name.startsWith(i.title)));
							item.description = r.Title;
							item.documentModified = dayjs(r.TimeLastModified);
							item.uniqueId = r.UniqueId;

							item.fileSize = r.Length;

							item.documentVersions = r.Versions.map ? r.Versions.map((history) => new DocumentVersion(history)) : r.Versions.results.map((history) => new DocumentVersion(history));
							item.documentVersions.push(new DocumentVersion(r, true));// current version

							// only for other documenys
							if (item.otherDocuments) item.title = r.Name.split('.')[0];
						});
						totalItems += response.length;
					});

					resolve(totalItems === items.length);
				}).catch((error) => {
					console.error('[PRODUCTS] Error at getSingleLibraryDocumentsDescriptions', error);
					reject(error);
				});
			});
		},

		getStaticData({ state, commit, getters }) {
			SharePointList.initialize({ siteCollectionPath: state.sites.intranetSiteColPath });

			// Gets everything inside Documentation and Products sites but not inside its desdendants (each series' sites)
			let restCallUrls = [
				VueResourceUrls.getDocumentLanguages(state.stateMachine.testMode), // 0 - site documentation
				VueResourceUrls.getDocumentationCategories(state.stateMachine.testMode), // 1 - products
				VueResourceUrls.getDocumentationSubcategories(state.stateMachine.testMode), // 2 - products
				VueResourceUrls.getSeriesStatus(state.stateMachine.testMode), // 3 - products
				VueResourceUrls.getFamilies(state.stateMachine.testMode), // 4 - products
				VueResourceUrls.getSeries(state.stateMachine.testMode), // 5 - products
				VueResourceUrls.getVersions(state.stateMachine.testMode), // 6 - products
				VueResourceUrls.getAllDocumentationLinksLists(state.stateMachine.testMode), // 7 - products
				VueResourceUrls.getSpUsers(), // 8 - intranet

				VueResourceUrls.getAllDocumentLibraries(state.stateMachine.testMode), // 9 - documentation
				VueResourceUrls.getDocumentationDefaultPackagesFilters(state.stateMachine.testMode), // 10 - products

				VueResourceUrls.getUserNotes(state.stateMachine.testMode), // 11 - playground/products
				VueResourceUrls.getUsageTracker(state.stateMachine.testMode), // 12 - intranet

				VueResourceUrls.getDocumentationCertifications(state.stateMachine.testMode), // 13 - products

				VueResourceUrls.getDocumentationCertificationsPackage(state.stateMachine.testMode), // 14 - products
			];

			return new Promise((resolve, reject) => {
				if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Starting getStaticData', restCallUrls);

				state.stateMachine.loadingInitialData = true;

				BatchPromise.call({ urls: { get: restCallUrls } }).then((response) => {
					state.languages = response[0].map((l) => new DocumentLanguage(l));
					state.spUsers = response[8].map((u) => new SPUser(u));
					state.documentationCategories = response[1].map((c) => new DocumentCategory(c));
					state.seriesStatus = response[3].map((ss) => new ProductSeriesStatus(ss));
					state.families = response[4].map((f) => new ProductFamily(f));

					// Reorder families so that child families (whose name starts with a parent's name + '-')
					// appear right after their parent family instead of at the end
					const reorderedFamilies = [];
					const placed = new Set();
					state.families.forEach((fam) => {
						if (placed.has(fam.id)) return;
						reorderedFamilies.push(fam);
						placed.add(fam.id);
						// Find child families whose name starts with this family's name + '-'
						state.families.forEach((child) => {
							if (!placed.has(child.id) && child.name !== fam.name && child.name.startsWith(`${fam.name}-`)) {
								reorderedFamilies.push(child);
								placed.add(child.id);
							}
						});
					});
					state.families = reorderedFamilies;

					state.documentationLinksLists = response[7].map((dll) => new DocumentationLinksList(dll));

					state.documentationDocumentLibraries = response[9].map((ddl) => new DocumentationLibrary(false, 'documentation/', ddl.EntityTypeName, ddl.Title, null, { high: ddl.EffectiveBasePermissions.High, low: ddl.EffectiveBasePermissions.Low }));
					state.documentationPackages = response[10].map((p) => new DocumentationPackage(p));

					state.documentationCertifications = response[13].map((c) => new DocumentCertification(c));
					state.documentationCertificationsPackages = response[14].map((c) => new DocumentationCertificationsPackages(c));

					AppsUsageItem.initialize({ spUsers: state.spUsers });

					DocumentSubcategory.initialize({
						categories: state.documentationCategories,
					});
					ProductSeries.initialize({
						families: state.families,
						statuses: state.seriesStatus,
					});

					state.documentationSubcategories = response[2].map((sc) => new DocumentSubcategory(sc));
					state.series = response[5].map((s) => new ProductSeries(s));

					DocumentationLinkItem.initialize({
						categories: state.documentationCategories,
						certifications: state.documentationCertifications,
						documentationSitePath: state.sites.documentationSitePath,
						languages: state.languages,
						spUsers: state.spUsers,
						subcategories: state.documentationSubcategories,
						tenantUrl: state.sites.tenantUrl,
					});
					ProductVersion.initialize({
						categories: state.documentationCategories,
						series: state.series,
					});
					DocumentVersion.initialize({ spUsers: state.spUsers });

					state.versions = response[6].map((v) => new ProductVersion(v));

					let tFam, tSer;

					state.series.forEach((ns) => {
						ns.status = state.seriesStatus.find((ss) => ss.id === ns.statusId);
						ns.family = tFam = state.families.find((nf) => nf.id === ns.familyId);
						if (tFam) tFam.series.push(ns);
					});
					state.families.forEach((nf) => {
						nf.series.sort(utilities.multiCriteriaSort('title'));
					});
					state.versions.forEach((nv) => {
						nv.series = tSer = state.series.find((ns) => ns.id === nv.seriesId);
						tSer.versions.push(nv);
						if (nv.includedInPendrive) {
							tSer.includedInPendrive = true;
						}
					});
					state.series.forEach((ns) => {
						ns.versions.sort(utilities.multiCriteriaSort('title', -1)); // Sorted alphabetically DESCENDING (latest version goes first)
					});

					state.series = state.series.filter((s) => s.family); // excluding EE
					state.versions = state.versions.filter((v) => v.series.family);

					state.documentationCategories.sort(utilities.multiCriteriaSort('title'));
					state.documentationSubcategories.sort(utilities.multiCriteriaSort('title'));

					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Finished getStaticData', state.documentTypes, state.languages, state.documentationCategories, state.documentationSubcategories, state.seriesStatus, state.families, state.series, state.versions);

					commit('setCurrentUser', getters);

					state.userNotes = response[11] ? response[11].map((un) => new UserNoteItem(un)).filter((un) => un.userId === state.currentUser.id).shift() : null;

					// disable usagetracker
					// state.usageTrackerListAllUsers = response[12] ? response[12].map((ut) => new AppsUsageItem(ut)) : null;

					// state.usageTrackerListAllUsers.filter((ut) => state.spUsers.find((spu) => spu.id === ut.userId));
					if (state.usageTrackerListAllUsers) {
						state.usageTrackerListAllUsers = state.usageTrackerListAllUsers.reduce((acum, ut) => {
							let user = state.spUsers.find((u) => u.id === ut.userId);
							if (user) {
								user.appUsageItems.push(ut);
								acum.push(ut);
							}
							return acum;
						}, []);
					}

					state.spUsers.forEach((u) => {
						if (u.appUsageItems.length === 0) u.appUsageItems = null;
					});

					state.usageTrackerList = state.usageTrackerListAllUsers ? state.usageTrackerListAllUsers.filter((ut) => ut.user.id === state.currentUser.id) : null;

					commit('setAllLanguagesIso6391');

					resolve();
				}).catch((error) => reject(error));
			});
		},

		getVersionContents({ state, dispatch }, { version }) {
			if (state.stateMachine.verboseMode) console.log('[PRODUCTS] Starting getVersionContents', version);

			let listTitle = `${version.series.url}_${version.title}`,
				documentationLinksList = state.documentationLinksLists.find((dll) => dll.title === listTitle),
				listItemEntityType = documentationLinksList && documentationLinksList.listItemEntityTypeFullName,
				effectivePermissions = documentationLinksList && documentationLinksList.effectivePermissions;

			return new Promise((resolve, reject) => {
				version.documentationLinksList = new SharePointList(true, ['playground/', 'products/DocumentationLinks/'], listTitle, listTitle, listItemEntityType, effectivePermissions);

				dispatch('getDocumentationLinkItems', { version }).then(() => {
					resolve();
				}).catch((error) => reject(error));
			});
		},

		saveDocumentationLinksListItem({ state, dispatch }, { item, versionsSelected, itemsIdArray, currentVersionId }) {
			return new Promise((resolve, reject) => {
				dispatch('getContextInfo').then(() => {
					const versionsFiltered = versionsSelected.map((vs) => state.versions.find((v) => v.id === vs.versionId)), // state.versions.filter((v) => versionsSelected.some((vs) => vs.versionId === v.id)),
						batchData = versionsFiltered.reduce((obj, v, index) => {
							let listTitle = `${v.series.url}_${v.title}`,
								documentationLinksList = state.documentationLinksLists.find((dll) => dll.title === listTitle),
								listItemEntityType = documentationLinksList && documentationLinksList.listItemEntityTypeFullName,
								effectivePermissions = documentationLinksList && documentationLinksList.effectivePermissions,
								spl = new SharePointList(true, ['playground/', 'products/DocumentationLinks/'], listTitle, listTitle, listItemEntityType, effectivePermissions);

							obj.urls = obj.urls || [];
							obj.urls.push(VueResourceUrls.saveDocumentationLinksListItem(store.state.stateMachine.testMode, spl, itemsIdArray[index] || 0));

							obj.data = obj.data || [];
							obj.data.push(VueResourceData.saveDocumentationLinksListItem(store.state.stateMachine.testMode, spl, item));

							obj.itemsId = obj.itemsId || [];
							obj.itemsId.push(itemsIdArray[index]);

							return obj;
						}, {});

					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] saveDocumentationLinksListItem starting save', batchData);

					BatchPromise.call({
						urls: {
							post: [batchData.urls],
						},
						data: {
							post: [batchData.data],
						},
						itemsId: batchData.itemsId,
					}).then((responseDLLI) => {
						if (state.stateMachine.verboseMode) console.log('[PRODUCTS] saveDocumentationLinksListItem.PInfo saved ok', responseDLLI);

						const versionAndDocumentObjects = state.versions.reduce((acum, v) => {
							if (v.documentationLinksList && v.documentationLinksList.content && versionsSelected.some((vs) => vs.versionId === v.id)) {
								acum.push({ version: v, documentLink: v.documentationLinksList.content.find((d) => d.id === item.id) });
							}
							return acum;
						}, []);

						item.linkModified = dayjs();

						versionAndDocumentObjects.forEach((vad) => {
							if (!vad.documentLink) {
								// if document doen't exits means that we are adding a new item
								// if versionId is equal at currentVersion don't do nothing, the view do a splice
								// batch call and batch responses have the same order than versionsSelected
								if (vad.version.id !== currentVersionId) {
									let itemCloned = item.clone(),
										index = versionsSelected.findIndex((vs) => vs.versionId === vad.version.id);

									itemCloned.id = responseDLLI[index].Id;
									itemCloned.relink();

									vad.version.documentationLinksList.content.push(itemCloned);
								} else {
									let index = versionsSelected.findIndex((vs) => vs.versionId === currentVersionId);// per evitar bugs
									item.id = responseDLLI[index].Id;
									item.relink();
								}
							} else if (vad.version.id !== currentVersionId) {
								vad.documentLink.subcategoryId = item.subcategoryId;
								vad.documentLink.languageId = item.languageId;
								vad.documentLink.pendrive = item.pendrive;
								vad.documentLink.relink();
							} else {
								item.relink();
							}
						});

						resolve(item);
					}).catch((error) => {
						console.error('[PRODUCTS] Error at saveDocumentationLinksListItem', error);
						reject(error);
					});
				}).catch((e) => { reject(e); });
			});
		},

		saveUserNotesItem({ state, dispatch }) {
			return new Promise((resolve, reject) => {
				dispatch('getContextInfo').then(() => {
					let restCallUrl = VueResourceUrls.saveUserNotesItem(state.stateMachine.testMode, state.userNotes.id),
						restCallData = VueResourceData.saveUserNotesItem(state.stateMachine.testMode, state.userNotes),
						restCallHeaders = VueResourceHeaders.standardSaveHeaders(state.userNotes.id);

					if (state.stateMachine.verboseMode) console.log('[PRODUCTS] saveUserNotesItem starting save', restCallUrl, restCallData, restCallHeaders);

					Vue.http.post(restCallUrl, restCallData, restCallHeaders).then((responseDLLI) => {
						if (state.stateMachine.verboseMode) console.log('[PRODUCTS] saveUserNotesItem.PInfo saved ok', responseDLLI);
						state.userNotes.id = state.userNotes.id || responseDLLI.body.d.ID;
						resolve();
					}).catch((error) => {
						console.error('[PRODUCTS] Error at saveUserNotesItem', error);
						reject(error);
					});
				}).catch((e) => { reject(e); });
			});
		},

		searchForUpdates({ state, getters }, fam) {
			if (state.stateMachine.testMode) console.warn('[PRODUCTS] searchForUpdates: REMBER TO USE TEST = 0');

			const families = fam ? [fam] : state.families.filter((f) => f.searchForUpdates),
				series = families.map((f) => {
					f.isSearchingForUpdates = true;
					return f;
				}).reduce((acum, family) => acum.concat(...family.series), []),
				versions = state.stateMachine.testMode ? state.versions.filter((v) => v.id === 9 || v.id === 78) : series.reduce((acum, s) => acum.concat(...s.versions), []),
				documentTitles = [],
				documentationLinkItemsUrls = versions.map((version) => {
					let listTitle = `${version.series.url}_${version.title}`,
						spl = new SharePointList(true, ['playground/', 'products/DocumentationLinks/'], listTitle, listTitle);
					return VueResourceUrls.searchForUpdates_getDocumentationLinks(state.stateMachine.testMode, spl);
				}),
				lastVisit = getters.getDaysForSearchUpdates;


			return new Promise((resolve) => {
				if (documentationLinkItemsUrls.length !== 0) {
					BatchPromise.call({ urls: { get: documentationLinkItemsUrls } }).then((responseGDLI) => {
						responseGDLI.forEach(async (documentLinks, index) => {
							versions[index].hasUpdates = documentLinks.some((doc) => dayjs(doc.Modified).isSameOrAfter(lastVisit, 'day'));
							documentTitles.push(documentLinks.map((d) => d.Title));
						});

						const documentsPendingToCheck = documentTitles.filter((dt, index) => !versions[index].hasUpdates).reduce((acum, dt) => acum.concat(dt), []);// series.filter((s) => s.hasUpdates === false).reduce((acum, s) => acum.concat((s.versions.reduce((acum, v) => acum.concat(v.versionContentUpdates), []))), []);

						let separator = '*delimiter*',
							documentsUniqueTitles = [...new Set(documentsPendingToCheck)],
							allLibraries = [...new Set(documentsUniqueTitles.map((title) => utilities.getSingleLibraryNameForDocumentTitle(title)))],
							libraryItemsArray = allLibraries.map((libraryName) => documentsUniqueTitles.filter((title) => utilities.getSingleLibraryNameForDocumentTitle(title) === libraryName)),
							filterArray = libraryItemsArray.map((libraryItems) => (encodeURIComponent(libraryItems.map((li) => `(Name eq '${li}.PDF')`).join(separator)).replaceAll("'", '%27')).split(separator)),
							spListArray = allLibraries.map((libraryName) => new SharePointList(false, 'documentation/', libraryName)),
							restCallUrlArray = spListArray.map((spList) => VueResourceUrls.searchForUpdates_getSingleLibraryDocumentsDescriptions(state.stateMachine.testMode, spList, ''));

						restCallUrlArray = restCallUrlArray.map((rcu, index) => {
							const { length } = rcu, // 133
								rcuSplited = rcu.split(/(?=&\$select=)/g), // https://stackoverflow.com/questions/12721844/javascript-split-without-losing-the-separator
								hostLength = 45; // https://salicru.sharepoint.com/sites/intranet lenght = 45
							let i = 0, urlAux = [];
							do {
								let filterAux = '', nextLength = 0;
								do {
									filterAux += `${(filterAux === '' ? '' : '%20or%20')}${filterArray[index][i++]}`;
									nextLength = filterArray[index][i] === undefined ? 0 : filterArray[index][i].length;
								} while (filterArray[index][i] && (filterAux.length + length + hostLength + nextLength) < 2048);
								urlAux.push(`${rcuSplited[0]}${filterAux}${rcuSplited[1]}`);
							} while (i < filterArray[index].length);
							return urlAux;
						});

						restCallUrlArray = restCallUrlArray.reduce((acum, rcu) => acum.concat(...rcu), []);

						BatchPromise.call({ urls: { get: restCallUrlArray } }).then((responseGSLDD) => {
							responseGSLDD.forEach((response) => {
								versions.forEach((v, index) => {
									if (!v.hasUpdates) {
										v.hasUpdates = response.some((r) => documentTitles[index].some((d) => r.Name.startsWith(d)) && dayjs(r.TimeLastModified).isSameOrAfter(lastVisit, 'day'));
									}
								});
							});
							families.forEach((f) => { f.isSearchingForUpdates = false; });
							state.searchForUpdatesDoneOnce = true;
							resolve();
						});
					});
				} else {
					resolve();
				}
			});
		},
	},
});
